import React, { useEffect, useMemo, useState } from 'react';
import { X, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Modal, Button, Select } from '../../../components';
import carePlannerService from '../../../services/carehome_manager/carePlannerService';
import {
  WEEKDAYS, FieldLabel, inputClass, toMinutes, fromMinutes, addDays, rotaWarning, dayLabel, errorMessage,
} from './shared';

/**
 * "Plan care" — schedule one or more care tasks for one resident across a
 * date range, on chosen weekdays, at one time, with one allocation.
 *
 * Every chosen task × every matching day becomes its own occurrence, so each
 * can later be reassigned, recorded or marked missed on its own. The preview
 * line says how many that is before anything is saved.
 *
 * In edit mode the dialog describes the plan's OPEN occurrences from today
 * (GET /plans/:id) and saving rewrites only those — the API keeps anything a
 * carer has started or recorded exactly as it was delivered. The resident is
 * fixed: care for somebody else is a new plan.
 *
 * The care tasks offered are the chosen resident's care-note actions — the
 * same categories and actions the app shows a carer recording a care note for
 * them, their own actions included (GET /residents/:id/care-actions). The
 * planner only reads that list; it never changes it.
 *
 * Rota checks are advisory. The rota is often incomplete, so a carer who
 * isn't rostered gets a warning, not a refusal.
 */

const ALL_DAYS = WEEKDAYS.map((d) => d.value);
const WEEKDAY_ONLY = [1, 2, 3, 4, 5];

const planDates = (from, to, weekdays) => {
  if (!from || !to || from > to) return [];
  const wanted = new Set(weekdays);
  const out = [];
  for (let d = from, i = 0; d <= to && i < 400; d = addDays(d, 1), i += 1) {
    if (wanted.has(new Date(`${d}T12:00:00`).getDay())) out.push(d);
  }
  return out;
};

const blankForm = (preset, today) => {
  const date = preset?.date && preset.date >= today ? preset.date : today;
  const start = preset?.time || '09:00';
  const startMin = toMinutes(start) ?? 540;
  const endMin = Math.min(startMin + 30, 1439);
  return {
    residenceId: preset?.residenceId ? String(preset.residenceId) : '',
    from: date,
    to: date,
    weekdays: ALL_DAYS,
    start,
    end: fromMinutes(endMin),
    minutes: String(endMin - startMin),
    staffRequired: '1',
    assignedTo: preset?.carerId ? String(preset.carerId) : '',
    secondAssignee: '',
    priority: 'routine',
    actions: preset?.actionCode ? [preset.actionCode] : [],
    instructions: '',
    notify: true,
  };
};

/* -------------------------------------------------------------- main - */

const PlanCareModal = ({ request, meta, rota, today, onClose, onSaved }) => {
  const isEdit = request?.kind === 'edit';
  const [form, setForm] = useState(() => blankForm(request?.preset, today));
  const [planInfo, setPlanInfo] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(isEdit);
  const [openCats, setOpenCats] = useState(() => new Set());
  const [actionSearch, setActionSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // What can be planned: the chosen resident's care-note actions (loaded
  // below), or the shared actions until a resident is chosen.
  const [library, setLibrary] = useState(meta?.library || []);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const carers = meta?.carers || [];
  const actionIndex = useMemo(() => {
    const map = new Map();
    library.forEach((c) => c.actions.forEach((a) => map.set(a.code, { ...a, category: c })));
    return map;
  }, [library]);

  useEffect(() => {
    if (!isEdit) {
      const preset = request?.preset;
      setForm(blankForm(preset, today));
      // Open the category of a pre-chosen action so the manager sees it ticked.
      if (preset?.actionCode) {
        const category = library.find((c) => c.actions.some((a) => a.code === preset.actionCode));
        if (category) setOpenCats(new Set([category.code]));
      }
      return undefined;
    }
    let live = true;
    setLoadingPlan(true);
    carePlannerService.getPlan(request.planId)
      .then((res) => {
        if (!live) return;
        const p = res.data;
        setPlanInfo(p);
        const minutes = (toMinutes(p.end_time) ?? 0) - (toMinutes(p.start_time) ?? 0);
        setForm({
          residenceId: String(p.residence_id),
          from: p.from,
          to: p.to,
          weekdays: p.weekdays,
          start: p.start_time,
          end: p.end_time,
          minutes: String(minutes),
          staffRequired: String(p.staff_required),
          assignedTo: p.assigned_to ? String(p.assigned_to) : '',
          secondAssignee: p.second_assignee ? String(p.second_assignee) : '',
          priority: p.priority,
          actions: p.actions,
          instructions: p.instructions,
          notify: true,
        });
      })
      .catch((err) => setError(errorMessage(err, 'Could not load this plan.')))
      .finally(() => live && setLoadingPlan(false));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // The resident's own care-note actions, whenever the resident changes.
  useEffect(() => {
    const residenceId = form.residenceId;
    if (!residenceId) {
      setLibrary(meta?.library || []);
      return undefined;
    }
    let live = true;
    setLoadingLibrary(true);
    carePlannerService.getCareActions(residenceId)
      .then((res) => {
        if (!live) return;
        const next = res.data || [];
        setLibrary(next);
        const selected = (codes) => next.filter((c) => c.actions.some((a) => codes.includes(a.code))).map((c) => c.code);
        setForm((f) => {
          // A new plan drops anything that was another resident's own action;
          // an edit keeps the plan as saved, so nothing disappears unseen.
          const codes = new Set(next.flatMap((c) => c.actions.map((a) => a.code)));
          const actions = isEdit ? f.actions : f.actions.filter((code) => codes.has(code));
          setOpenCats((open) => new Set([...open, ...selected(actions)]));
          return actions.length === f.actions.length ? f : { ...f, actions };
        });
      })
      .catch((err) => live && setError(errorMessage(err, 'Could not load this resident’s care actions.')))
      .finally(() => live && setLoadingLibrary(false));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.residenceId]);

  const set = (patch) => {
    setError('');
    setForm((f) => ({ ...f, ...patch }));
  };

  // Start, end and minutes stay in step: whichever is edited, the others follow.
  const onStart = (value) => {
    const s = toMinutes(value);
    const m = Number(form.minutes);
    if (s !== null && m > 0 && s + m < 1440) set({ start: value, end: fromMinutes(s + m) });
    else set({ start: value });
  };
  const onEnd = (value) => {
    const s = toMinutes(form.start);
    const e = toMinutes(value);
    set({ end: value, minutes: s !== null && e !== null && e > s ? String(e - s) : '' });
  };
  const onMinutes = (value) => {
    const s = toMinutes(form.start);
    const m = Number(value);
    set({ minutes: value, ...(s !== null && m > 0 && s + m < 1440 ? { end: fromMinutes(s + m) } : {}) });
  };

  const toggleDay = (day) => set({
    weekdays: form.weekdays.includes(day) ? form.weekdays.filter((d) => d !== day) : [...form.weekdays, day],
  });
  const toggleAction = (code) => set({
    actions: form.actions.includes(code) ? form.actions.filter((c) => c !== code) : [...form.actions, code],
  });

  const dates = useMemo(() => planDates(form.from, form.to, form.weekdays), [form.from, form.to, form.weekdays]);
  const occurrences = dates.length * form.actions.length;
  const two = form.staffRequired === '2';

  const carerOptions = [
    { value: '', label: 'Leave unassigned' },
    ...carers.map((c) => ({ value: String(c.id), label: `${c.name} · ${c.job_title}` })),
  ];
  const carerById = (id) => carers.find((c) => String(c.id) === String(id));
  const warnings = [form.assignedTo, two ? form.secondAssignee : '']
    .filter(Boolean)
    .map((id) => rotaWarning(rota, carerById(id), dates, form.start, form.end))
    .filter(Boolean);

  const residentOptions = (meta?.residents || []).map((r) => ({
    value: String(r.id),
    label: r.room ? `${r.name} · Room ${r.room}` : r.name,
  }));
  if (isEdit && planInfo?.resident && !residentOptions.some((o) => o.value === String(planInfo.residence_id))) {
    residentOptions.push({ value: String(planInfo.residence_id), label: planInfo.resident.name });
  }

  const residentFirstName = ((meta?.residents || []).find((r) => String(r.id) === form.residenceId)?.name || '').split(' ')[0];

  const validate = () => {
    if (!form.residenceId) return 'Choose a resident.';
    if (!form.actions.length) return 'Choose at least one care task.';
    if (!form.from || !form.to) return 'Choose a from and to date.';
    if (form.from > form.to) return 'The to date must be on or after the from date.';
    if (form.from < today) return 'Plan care from today onwards.';
    if (!form.weekdays.length) return 'Choose at least one day to repeat on.';
    if (!dates.length) return 'None of the chosen days fall between those dates.';
    const s = toMinutes(form.start);
    const e = toMinutes(form.end);
    if (s === null || e === null) return 'Enter a start and end time.';
    if (e <= s) return 'The end time must be after the start time. Plan care that runs past midnight as two tasks.';
    if (two && Boolean(form.assignedTo) !== Boolean(form.secondAssignee)) {
      return 'Two-person care needs both carers named — or leave both unassigned.';
    }
    if (two && form.assignedTo && form.assignedTo === form.secondAssignee) return 'Choose two different carers.';
    if (!form.instructions.trim()) return 'Add instructions for this resident.';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const payload = {
      residence_id: Number(form.residenceId),
      actions: form.actions,
      from: form.from,
      to: form.to,
      weekdays: form.weekdays,
      start_time: form.start,
      end_time: form.end,
      staff_required: Number(form.staffRequired),
      assigned_to: form.assignedTo ? Number(form.assignedTo) : null,
      second_assignee: two && form.secondAssignee ? Number(form.secondAssignee) : null,
      priority: form.priority,
      instructions: form.instructions,
      notify: form.notify,
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await carePlannerService.updatePlan(request.planId, payload)
        : await carePlannerService.createPlan(payload);
      onSaved(res.message || 'Care planned.', dates[0]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const q = actionSearch.trim().toLowerCase();
  const visibleCategories = library
    .map((c) => ({
      ...c,
      shown: c.actions.filter((a) => !q || a.label.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)),
    }))
    .filter((c) => !q || c.shown.length);

  const editLocked = isEdit && planInfo && !planInfo.editable;

  return (
    <Modal isOpen onClose={onClose} hideHeader maxWidth="max-w-5xl" bodyClassName="flex flex-col">
      <form onSubmit={submit} className="flex flex-col">
        <header className="flex items-start justify-between gap-4 px-6 md:px-8 pt-6 pb-4 border-b border-[#EDF1F7] dark:border-gray-800">
          <div>
            <h2 className="text-[22px] font-extrabold tracking-[-0.4px] text-[#0B1220] dark:text-gray-100">
              {isEdit ? 'Edit planned care' : 'Plan care'}
            </h2>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1">
              {isEdit
                ? 'Change the schedule, tasks or staff for the care still to come.'
                : 'Choose the tasks, set the schedule and assign staff.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 grid place-items-center rounded-full border border-[#DCE4EF] dark:border-gray-700 text-[#64748B] hover:text-[#0B1220] dark:hover:text-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {loadingPlan ? (
          <div className="px-8 py-16 text-center text-[13px] text-[#64748B]">Loading the plan…</div>
        ) : (
          <div className="px-6 md:px-8 py-6 grid lg:grid-cols-2 gap-6">
            {/* ── Resident & schedule ── */}
            <section className="rounded-[18px] border border-[#DCE6F5] dark:border-gray-800 bg-[#F5F8FF] dark:bg-[#20242c] p-5 space-y-4">
              <h3 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">Resident & schedule</h3>

              <div>
                <FieldLabel required>Resident</FieldLabel>
                <Select
                  value={form.residenceId}
                  onChange={(v) => set({ residenceId: v })}
                  options={residentOptions}
                  placeholder="Select resident"
                  disabled={isEdit}
                  className="w-full"
                />
                {isEdit && <p className="text-[11.5px] text-[#94A3B8] mt-1">The resident is fixed for a plan. Plan care for somebody else as a new plan.</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel htmlFor="cp-from" required>From date</FieldLabel>
                  <input id="cp-from" type="date" min={today} className={inputClass} value={form.from}
                    onChange={(e) => set({ from: e.target.value, ...(form.to < e.target.value ? { to: e.target.value } : {}) })} />
                </div>
                <div>
                  <FieldLabel htmlFor="cp-to" required>To date</FieldLabel>
                  <input id="cp-to" type="date" min={form.from || today} className={inputClass} value={form.to} onChange={(e) => set({ to: e.target.value })} />
                </div>
              </div>

              <fieldset>
                <legend className="text-[12px] font-semibold text-[#334155] dark:text-gray-300 mb-1.5">Repeat on</legend>
                <div className="flex gap-3 mb-2 text-[12px] font-semibold">
                  <button type="button" className="text-blue-600 hover:underline" onClick={() => set({ weekdays: ALL_DAYS })}>Every day</button>
                  <button type="button" className="text-blue-600 hover:underline" onClick={() => set({ weekdays: WEEKDAY_ONLY })}>Weekdays</button>
                  <button type="button" className="text-blue-600 hover:underline" onClick={() => set({ weekdays: [] })}>Clear</button>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {WEEKDAYS.map((d) => {
                    const on = form.weekdays.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleDay(d.value)}
                        className={`h-9 rounded-[9px] text-[12px] font-bold transition-colors ${
                          on ? 'bg-blue-600 text-white' : 'bg-white dark:bg-[#262b33] text-[#64748B] border border-[#DCE4EF] dark:border-gray-700 hover:border-blue-300'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="grid grid-cols-[1fr_1fr_90px] gap-3">
                <div>
                  <FieldLabel htmlFor="cp-start" required>Start time</FieldLabel>
                  <input id="cp-start" type="time" className={inputClass} value={form.start} onChange={(e) => onStart(e.target.value)} />
                </div>
                <div>
                  <FieldLabel htmlFor="cp-end" required>End time</FieldLabel>
                  <input id="cp-end" type="time" className={inputClass} value={form.end} onChange={(e) => onEnd(e.target.value)} />
                </div>
                <div>
                  <FieldLabel htmlFor="cp-mins">Minutes</FieldLabel>
                  <input id="cp-mins" type="number" min={1} max={1439} className={inputClass} value={form.minutes} onChange={(e) => onMinutes(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Staff required</FieldLabel>
                  <Select
                    value={form.staffRequired}
                    onChange={(v) => set({ staffRequired: v, ...(v === '1' ? { secondAssignee: '' } : {}) })}
                    options={[{ value: '1', label: '1 care worker' }, { value: '2', label: '2 care workers' }]}
                    className="w-full"
                  />
                </div>
                <div>
                  <FieldLabel>{two ? 'First carer' : 'Assign carer'}</FieldLabel>
                  <Select value={form.assignedTo} onChange={(v) => set({ assignedTo: v })} options={carerOptions} className="w-full" />
                </div>
              </div>
              {two && (
                <div>
                  <FieldLabel>Second carer</FieldLabel>
                  <Select
                    value={form.secondAssignee}
                    onChange={(v) => set({ secondAssignee: v })}
                    options={carerOptions.filter((o) => !o.value || o.value !== form.assignedTo)}
                    className="w-full"
                  />
                </div>
              )}
              {warnings.map((w) => (
                <p key={w} className="text-[12px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 rounded-lg px-3 py-2">{w}</p>
              ))}
              {!carers.length && (
                <p className="text-[12px] text-[#64748B]">No carers to allocate yet. Care can be planned unassigned and allocated later.</p>
              )}

              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <FieldLabel>Priority</FieldLabel>
                  <Select
                    value={form.priority}
                    onChange={(v) => set({ priority: v })}
                    options={(meta?.priorities || []).map((p) => ({ value: p.value, label: p.label }))}
                    className="w-full"
                  />
                </div>
                <label className="flex items-center gap-2 h-10 text-[12.5px] font-semibold text-[#334155] dark:text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={form.notify} onChange={(e) => set({ notify: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                  Notify assigned carer(s)
                </label>
              </div>
              <p className="text-[11.5px] text-[#94A3B8] -mt-2">Priority is shown to the carer in My Shift.</p>
            </section>

            {/* ── Care tasks & instructions ── */}
            <section className="space-y-4 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">Care tasks & instructions</h3>
                {form.actions.length > 0 && (
                  <button type="button" className="text-[12px] font-semibold text-[#64748B] hover:text-rose-600" onClick={() => set({ actions: [] })}>
                    Clear {form.actions.length} selected
                  </button>
                )}
              </div>

              {form.actions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {form.actions.map((code) => (
                    <span key={code} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-blue-600 text-white text-[12px] font-semibold">
                      {actionIndex.get(code)?.label || code}
                      <button type="button" aria-label="Remove" onClick={() => toggleAction(code)} className="w-5 h-5 grid place-items-center rounded hover:bg-white/20">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="rounded-[14px] border border-[#DCE4EF] dark:border-gray-700 overflow-hidden">
                <div className="flex items-center gap-2 px-3 h-11 border-b border-[#EDF1F7] dark:border-gray-800">
                  <Search className="w-4 h-4 text-[#94A3B8]" />
                  <input
                    value={actionSearch}
                    onChange={(e) => setActionSearch(e.target.value)}
                    placeholder="Find a care task"
                    aria-label="Find a care task"
                    className="flex-1 bg-transparent text-[13px] text-[#0B1220] dark:text-gray-100 placeholder-[#A0AEC0] focus:outline-none"
                  />
                </div>
                <div className="max-h-[340px] overflow-y-auto divide-y divide-[#EDF1F7] dark:divide-gray-800">
                  {visibleCategories.map((c) => {
                    const open = q ? true : openCats.has(c.code);
                    const picked = c.actions.filter((a) => form.actions.includes(a.code)).length;
                    return (
                      <div key={c.code}>
                        <button
                          type="button"
                          onClick={() => setOpenCats((s) => {
                            const next = new Set(s);
                            if (next.has(c.code)) next.delete(c.code); else next.add(c.code);
                            return next;
                          })}
                          className="w-full flex items-center gap-2 px-3.5 py-3 text-left text-[13px] font-semibold text-[#0B1220] dark:text-gray-100 hover:bg-[#F7F9FC] dark:hover:bg-[#2b3038]"
                        >
                          {open ? <ChevronDown className="w-4 h-4 text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
                          <span className="flex-1">{c.name}</span>
                          {picked > 0 && <span className="text-[11px] font-bold text-blue-600 bg-[#EEF3FF] dark:bg-blue-900/20 rounded-md px-1.5 py-0.5">{picked}</span>}
                        </button>
                        {open && (
                          <div className="px-3.5 pb-3 flex flex-wrap gap-1.5">
                            {c.shown.map((a) => {
                              const on = form.actions.includes(a.code);
                              return (
                                <button
                                  key={a.code}
                                  type="button"
                                  aria-pressed={on}
                                  onClick={() => toggleAction(a.code)}
                                  title={a.own ? `Added for ${residentFirstName || 'this resident'} in their care notes` : undefined}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors ${
                                    on
                                      ? 'bg-blue-600 border-blue-600 text-white'
                                      : 'bg-white dark:bg-[#262b33] border-[#DCE4EF] dark:border-gray-700 text-[#334155] dark:text-gray-300 hover:border-blue-300'
                                  }`}
                                >
                                  {a.label}
                                  {a.own && (
                                    <span className={`text-[10px] font-bold px-1.5 py-px rounded ${
                                      on ? 'bg-white/20 text-white' : 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                    }`}
                                    >
                                      {residentFirstName || 'Own'}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {loadingLibrary && <p className="px-4 py-6 text-[12.5px] text-[#94A3B8]">Loading {residentFirstName ? `${residentFirstName}’s` : 'the'} care actions…</p>}
                  {!loadingLibrary && !visibleCategories.length && (
                    <p className="px-4 py-6 text-[12.5px] text-[#94A3B8]">
                      {q ? `No care tasks match “${actionSearch}”.` : 'No care-note actions are set up yet.'}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-[11.5px] text-[#94A3B8] -mt-2">
                {form.residenceId
                  ? `The same actions carers see in ${residentFirstName || 'this resident'}’s care notes, their own ones included. Dates, time, staff and instructions apply to all selected tasks.`
                  : 'Choose a resident to see their own care-note actions too. Dates, time, staff and instructions apply to all selected tasks.'}
              </p>

              <div>
                <FieldLabel htmlFor="cp-instructions" required>Resident-specific instructions</FieldLabel>
                <textarea
                  id="cp-instructions"
                  rows={6}
                  maxLength={2000}
                  className={`${inputClass} h-auto py-2.5 resize-y leading-relaxed`}
                  value={form.instructions}
                  onChange={(e) => set({ instructions: e.target.value })}
                  placeholder="Describe what staff should do for this resident, using their agreed care plan."
                />
              </div>
            </section>
          </div>
        )}

        <div className="px-6 md:px-8">
          <p aria-live="polite" className="rounded-xl bg-[#F2F6FF] dark:bg-blue-900/15 border border-[#DCE6F9] dark:border-blue-900/40 text-[#1E4FD8] dark:text-blue-300 text-[13px] font-medium px-4 py-3">
            {occurrences} care occurrence{occurrences === 1 ? '' : 's'} · {form.actions.length} task{form.actions.length === 1 ? '' : 's'} × {dates.length} day{dates.length === 1 ? '' : 's'}
            {dates.length > 0 && ` · ${dayLabel(dates[0])}${dates.length > 1 ? ` → ${dayLabel(dates[dates.length - 1])}` : ''}`}
          </p>
          {isEdit && planInfo && (
            <p className="mt-2 text-[12px] text-[#64748B] dark:text-gray-400">
              {planInfo.editable
                ? `Saving rewrites the ${planInfo.open_count} occurrence${planInfo.open_count === 1 ? '' : 's'} still to come.${planInfo.kept_count ? ` ${planInfo.kept_count} started, recorded or past ${planInfo.kept_count === 1 ? 'occurrence is' : 'occurrences are'} kept as they were.` : ''}`
                : 'Every occurrence of this plan has been started, recorded or has passed, so there is nothing left to edit.'}
            </p>
          )}
          {error && <p role="alert" className="mt-2 text-[13px] font-medium text-rose-600">{error}</p>}
        </div>

        <footer className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 md:px-8 py-5 mt-4 border-t border-[#EDF1F7] dark:border-gray-800">
          <small className="text-[12px] text-[#64748B] dark:text-gray-400">
            Planned care appears here and in each assigned carer’s My Shift.
          </small>
          <div className="flex gap-2.5 justify-end">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={saving} disabled={loadingPlan || editLocked}>
              {isEdit ? 'Save changes' : 'Add scheduled care'}
            </Button>
          </div>
        </footer>
      </form>

    </Modal>
  );
};

export default PlanCareModal;
